<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {ElButton, ElButtonGroup} from 'element-plus';
import {DashboardReeksEnum, DashboardReeksEnum_name, formatWaarde, QUADRUPLE_AIM_REEKSEN, ReeksenPage, ReeksNotitie, ReeksSerie, SCORE_REEKSEN, ZZC_TOTAAL_LABEL} from 'common';
import {dashboardReeksen} from '@/base/callBackend.js';
import {dashboardOrgs, dashboardPeriod, laadDashboard} from '@/base/dashboardState.js';
import {localStorageGetJson, localStorageSetJson} from '@/base/utils.js';
import KpiCard from '@/views/dashboard/KpiCard.vue';
import ReeksChart from '@/views/dashboard/ReeksChart.vue';
import ReeksNotities from '@/views/dashboard/ReeksNotities.vue';

const STORAGE_SCORE_ID: string = 'dashboard:quadrupleaim:score';
const SECTIES: DashboardReeksEnum[] = [
    DashboardReeksEnum.volume,
    DashboardReeksEnum.productiviteit,
    DashboardReeksEnum.omzet,
    DashboardReeksEnum.schadelast,
];
const VOETNOTEN: string[] = [
    'N.B. SVRZ telt ook mee in het volume van ZiB.',
    'N.B. Baselines opgeteld (ZiB is van 25Q1).',
    'Zie dataverklaring voor een uitleg van de getoonde getallen.',
];

const loading = ref<boolean>(true);
const page    = ref<ReeksenPage>({series: [], zzc: [], notities: []});
const score   = ref<DashboardReeksEnum>(SCORE_REEKSEN.includes(localStorageGetJson(STORAGE_SCORE_ID)) ? localStorageGetJson(STORAGE_SCORE_ID) : DashboardReeksEnum.medewerkerstevredenheid);
let loadSeq: number = 0;

const eenGroep = computed<boolean>(() => dashboardOrgs.value.length === 1);
const secties  = computed<DashboardReeksEnum[]>(() => [...SECTIES, score.value]);

watch(score, (s: DashboardReeksEnum) => localStorageSetJson(STORAGE_SCORE_ID, s));
watch([dashboardPeriod, dashboardOrgs], load, {immediate: true});

async function load() {
    if (dashboardPeriod.value === '') {
        return;
    }
    const seq: number                    = ++loadSeq;
    loading.value                        = true;
    const result: ReeksenPage | undefined = await laadDashboard(() => dashboardReeksen(dashboardPeriod.value, dashboardOrgs.value, QUADRUPLE_AIM_REEKSEN));
    if (seq !== loadSeq) {
        return;
    }
    page.value    = result ?? {series: [], zzc: [], notities: []};
    loading.value = false;
}

function seriesVan(reeks: DashboardReeksEnum): ReeksSerie[] {
    return page.value.series.filter(s => s.reeks === reeks);
}

function zzcVan(reeks: DashboardReeksEnum): ReeksSerie | undefined {
    return page.value.zzc.find(s => s.reeks === reeks);
}

function notitiesVan(reeks: DashboardReeksEnum): ReeksNotitie[] {
    return page.value.notities.filter(n => n.reeks === reeks);
}

function eersteMeting(s: ReeksSerie): string {
    return `${formatWaarde(s.punten[0].waarde, s.waardeType)} (${s.punten[0].kwartaal})`;
}
</script>

<template>
    <div class="px-10 py-4 flex flex-col gap-8">
        <div v-if="loading" class="text-sm text-gray-500">laden...</div>

        <section v-for="reeks in secties" :key="reeks" class="flex flex-col gap-2">
            <div class="flex flex-row items-center gap-4">
                <div class="text-xl font-semibold text-zzc-blue">{{ DashboardReeksEnum_name(reeks) }}</div>
                <el-button-group v-if="SCORE_REEKSEN.includes(reeks)">
                    <el-button v-for="r in SCORE_REEKSEN" :key="r" size="small" :type="score === r ? 'primary' : 'default'" @click="score = r">
                        {{ DashboardReeksEnum_name(r) }}
                    </el-button>
                </el-button-group>
            </div>

            <div v-if="!loading && seriesVan(reeks).length === 0 && !zzcVan(reeks)" class="text-sm text-gray-500">Geen gegevens voor deze selectie.</div>

            <div v-else-if="eenGroep && seriesVan(reeks).length === 1" class="grid grid-cols-5 gap-4">
                <div class="col-span-3">
                    <ReeksChart :serie="seriesVan(reeks)[0]" type="line"/>
                </div>
                <div class="col-span-2 flex flex-col gap-2">
                    <KpiCard label="Eerste meting" :value="eersteMeting(seriesVan(reeks)[0])"/>
                    <KpiCard v-if="seriesVan(reeks)[0].target" label="Target" :value="seriesVan(reeks)[0].target!.omschrijving"/>
                    <ReeksNotities :notities="notitiesVan(reeks)" :reeksen="[reeks]"/>
                </div>
            </div>

            <div v-else class="grid grid-cols-3 gap-4">
                <div v-for="s in seriesVan(reeks)" :key="s.organisatie" class="rounded border border-gray-200 bg-white p-2">
                    <div class="text-sm font-semibold">{{ s.organisatie }}</div>
                    <ReeksChart :serie="s" type="line" compact/>
                </div>
                <div v-if="zzcVan(reeks)" class="rounded border border-zzc-blue bg-white p-2">
                    <div class="text-sm font-semibold">{{ ZZC_TOTAAL_LABEL }}</div>
                    <ReeksChart :serie="zzcVan(reeks)!" type="line" compact/>
                </div>
            </div>
        </section>

        <div class="text-xs text-gray-500 flex flex-col gap-1">
            <div v-for="v in VOETNOTEN" :key="v">{{ v }}</div>
        </div>
    </div>
</template>
